import { nomeDoProvedorOpencode, type EstadoDaConfigOpencode, type MaquinaNoEstadoOpencode } from '@remoto/shared';
import { Cartao } from '../componentes/layout';
import { tempoRelativo } from '../formato';
import {
  processosDoRetrato,
  ROTULO_DA_CAMADA,
  ROTULO_DA_SITUACAO,
  situacaoDaChave,
  textoDasDesatualizadas,
  textoDoAgregado,
  textoDoProcesso,
  tomDaSituacao,
  type SituacaoDaChave,
} from './regras';

const COR_DO_TOM: Record<ReturnType<typeof tomDaSituacao>, string> = {
  bom: 'bg-emerald-500/10 text-emerald-300',
  espera: 'bg-amber-500/10 text-amber-200',
  ruim: 'bg-red-500/10 text-red-300',
  neutro: 'bg-zinc-800 text-zinc-400',
};

/**
 * As colunas no desktop, numa definição só para o cabeçalho e as linhas.
 *
 * No telefone não há tabela: cada máquina vira um cartão de duas colunas, e
 * cada célula diz o próprio nome (`Celula`) — lá não há cabeçalho, e "345 MB"
 * solto no cartão não diz de quê. É o desenho da tela de Uso.
 */
const GRADE =
  'lg:grid lg:grid-cols-[minmax(0,1.5fr)_minmax(0,0.8fr)_minmax(0,1.7fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1.6fr)] lg:items-start lg:gap-3';

function Celula({
  rotulo,
  children,
  className,
}: {
  rotulo: string;
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div className={'min-w-0 text-xs text-zinc-400 ' + (className ?? '')}>
      <span className="text-zinc-600 lg:sr-only">{rotulo} </span>
      {children}
    </div>
  );
}

function SeloDaSituacao({ situacao }: { situacao: SituacaoDaChave }): React.JSX.Element {
  return (
    <span
      data-testid="situacao-da-chave"
      data-situacao={situacao}
      className={'inline-flex rounded-full px-2 py-0.5 text-[11px] ' + COR_DO_TOM[tomDaSituacao(situacao)]}
    >
      {ROTULO_DA_SITUACAO[situacao]}
    </span>
  );
}

function LinhaDaMaquina({
  maquina,
  versaoDaFrota,
}: {
  maquina: MaquinaNoEstadoOpencode;
  /** A versão do FIO (`versaoDaCredencial`), ou `null` sem chave do provedor ativo. */
  versaoDaFrota: number | null;
}): React.JSX.Element {
  const retrato = maquina.opencode;
  const situacao = situacaoDaChave(versaoDaFrota, retrato, maquina.desatualizada);
  const processos = retrato ? processosDoRetrato(retrato) : [];

  return (
    <li
      data-testid="maquina-opencode"
      data-maquina={maquina.nome}
      className={
        // `dense` no telefone: a célula de uma coluna que vem depois de uma
        // de duas preenche o buraco que ficaria ao lado da versão.
        'grid grid-flow-row-dense grid-cols-2 gap-x-3 gap-y-1.5 rounded-lg border border-zinc-800/70 p-3 ' +
        'lg:rounded-none lg:border-0 lg:border-t lg:px-2 lg:py-2 ' +
        GRADE
      }
    >
      <div className="col-span-2 min-w-0 lg:col-span-1">
        <p className="flex min-w-0 items-center gap-1.5 text-sm text-zinc-100">
          <span
            aria-hidden="true"
            className={'h-1.5 w-1.5 shrink-0 rounded-full ' + (maquina.online ? 'bg-emerald-400' : 'bg-zinc-600')}
          />
          <span className="truncate">{maquina.nome}</span>
        </p>
        <p className="truncate text-[11px] text-zinc-500">
          {maquina.papel === 'VPS' ? 'VPS' : 'PC'} · {maquina.online ? 'no ar' : 'desligada'}
          {/*
            Máquina desligada mostra o ÚLTIMO retrato — e diz de quando é. Sem a
            hora, "de pé" numa máquina desligada pareceria mentira.
          */}
          {retrato && !maquina.online ? ' · retrato ' + tempoRelativo(retrato.medidoEm) : ''}
        </p>
      </div>

      <Celula rotulo="versão">{retrato?.versao ?? (retrato ? 'não apurada' : 'sem OpenCode')}</Celula>

      <Celula rotulo="processos" className="col-span-2 lg:col-span-1">
        {!retrato ? (
          '—'
        ) : processos.length === 0 ? (
          'nenhum processo'
        ) : (
          <span className="flex flex-col">
            {processos.map((p) => (
              <span key={p.perfil} className={'truncate ' + (p.dePe ? 'text-zinc-300' : 'text-amber-200')}>
                {textoDoProcesso(p)}
              </span>
            ))}
          </span>
        )}
      </Celula>

      <Celula rotulo="chave">
        <SeloDaSituacao situacao={situacao} />
        {retrato?.chave.versao ? (
          <span className="ml-1 text-[11px] text-zinc-500">v{retrato.chave.versao}</span>
        ) : null}
      </Celula>

      <Celula rotulo="camada gerenciada">
        {retrato ? (
          <span
            data-testid="camada-gerenciada"
            className={retrato.camadaGerenciada === 'ok' ? 'text-zinc-300' : 'text-red-300'}
          >
            {ROTULO_DA_CAMADA[retrato.camadaGerenciada]}
          </span>
        ) : (
          '—'
        )}
      </Celula>

      <Celula rotulo="último erro" className="col-span-2 lg:col-span-1">
        {/* O erro já chega redigido: o schema do retrato passa ele pela redação no parse. */}
        <span className={'break-words ' + (retrato?.erro ? 'text-red-300' : '')}>{retrato?.erro ?? '—'}</span>
      </Celula>
    </li>
  );
}

/**
 * O OpenCode em cada máquina (D17): versão, processos, RSS, sessões, a chave,
 * a camada gerenciada e o último erro — e, acima, o "aplicada em X de Y" que o
 * dono espera ver fechar depois de trocar a chave (D9). A máquina com daemon
 * anterior ao que fala o provedor ativo (F2) aparece como "daemon
 * desatualizado", nunca como "sem chave".
 *
 * Ligar ou desligar o OpenCode numa máquina NÃO mora aqui: continua no `.env`
 * do daemon daquela máquina (D17), e a tela diz isso em vez de parecer que
 * esqueceu o botão.
 */
export function SecaoDeMaquinas({ estado }: { estado: EstadoDaConfigOpencode }): React.JSX.Element {
  const { aplicadas, chave, maquinas } = estado;
  const completa = chave !== null && aplicadas.elegiveis > 0 && aplicadas.aplicadas === aplicadas.elegiveis;
  /*
    A versão contra a qual o "aplicada" se mede é a do FIO (F2): a soma das
    duas chaves com a parcela da configuração — é ela que o daemon guarda.
    Sem chave do provedor ativo, não há o que aplicar.
  */
  const versaoDaFrota = chave === null ? null : estado.versaoDaCredencial;
  const desatualizadas = textoDasDesatualizadas(
    maquinas.filter((m) => m.desatualizada).length,
    nomeDoProvedorOpencode(estado.config.provedor),
  );

  return (
    <Cartao titulo="Máquinas">
      <div className="space-y-3">
        <p
          data-testid="agregado-da-chave"
          className={'text-sm ' + (completa ? 'text-emerald-300' : chave ? 'text-amber-200' : 'text-zinc-400')}
        >
          {textoDoAgregado(aplicadas, chave !== null)}
        </p>
        {desatualizadas ? (
          <p data-testid="maquinas-desatualizadas" className="text-xs text-red-300">
            {desatualizadas}
          </p>
        ) : null}

        {maquinas.length === 0 ? (
          <p className="text-sm text-zinc-500">Nenhuma máquina cadastrada.</p>
        ) : (
          <>
            <div
              aria-hidden="true"
              className={'hidden px-2 pb-1 text-[11px] uppercase tracking-wide text-zinc-600 ' + GRADE}
            >
              <span>máquina</span>
              <span>versão</span>
              <span>processos</span>
              <span>chave</span>
              <span>camada</span>
              <span>último erro</span>
            </div>
            {/*
              Lista que cresce com a frota: teto em `vh` e rolagem própria
              (`web.md`). Sem o teto, uma frota grande empurraria o resto da
              tela para baixo da dobra no telefone.
            */}
            <ul data-testid="lista-de-maquinas" className="max-h-[60vh] space-y-2 overflow-y-auto lg:space-y-0">
              {maquinas.map((m) => (
                <LinhaDaMaquina key={m.maquinaId} maquina={m} versaoDaFrota={versaoDaFrota} />
              ))}
            </ul>
          </>
        )}

        <p className="text-[11px] text-zinc-500">
          Ligar ou desligar o OpenCode numa máquina é no <span className="font-mono">.env</span> do
          daemon dela (<span className="font-mono">OPENCODE_HABILITADO</span>), não aqui.
        </p>
      </div>
    </Cartao>
  );
}
